// ─────────────────────────────────────────────────────────────────
//  patientTokens.js  —  Atlantis Pediatric Patient Portal
//  "Clinical Clarity" design system v3
//  v3 adds: brand gradients, glow shadows, glass tokens, hero styles,
//  skeleton shimmer, timeline styles. 100% backwards-compatible with v2.
// ─────────────────────────────────────────────────────────────────

// ── Brand blue ─────────────────────────────────────────────────
export const G900 = "#002D6B";
export const G800 = "#00419E";
export const G700 = "#0E5BC2";   // PRIMARY
export const G600 = "#2470D1";
export const G500 = "#4189DC";
export const G400 = "#6AAEE8";
export const G200 = "#C2DCF8";
export const G100 = "#DBEEFF";
export const G50  = "#F0F7FF";

// ── NEW v3: cyan accent (used sparingly — hero glow, live dots) ──
export const CYAN     = "#22D3EE";
export const CYAN_SOFT = "rgba(34,211,238,0.16)";

// ── Neutrals ──────────────────────────────────────────────────
export const SLATE  = "#0F1C2E";
export const SLATE2 = "#2C3E58";
export const MUTED  = "#5E718D";
export const GRAY2  = "#8FA3BC";
export const GRAY4  = "#C5D2DF";
export const GRAY6  = "#E3EBF3";
export const GRAY7  = "#F0F4F8";
export const SURF   = "#F6F9FC";
export const WHITE  = "#FFFFFF";

// ── Semantic ──────────────────────────────────────────────────
export const RED        = "#E53E3E";
export const RED_BG     = "#FFF5F5";
export const RED_BORDER = "#FEB2B2";

export const AMBER        = "#C47020";
export const AMBER_MID    = "#DD8D2A";
export const AMBER_BG     = "#FFFBF0";
export const AMBER_BORDER = "#F6D48B";

export const GREEN        = "#1A7F52";
export const GREEN_MID    = "#22A66A";
export const GREEN_BG     = "#F0FBF6";
export const GREEN_BORDER = "#9EE6C4";

export const BLUE        = "#1D5FA8";
export const BLUE_MID    = "#2E7DD4";
export const BLUE_BG     = "#EFF7FF";
export const BLUE_BORDER = "#93C5FD";

export const PURPLE    = "#6D3ABF";
export const PURPLE_BG = "#F5F0FF";

export const VISIT_STATUS = {
  OPEN:    { color: AMBER, border: AMBER_BORDER, bg: AMBER_BG, label: "Open"    },
  CLOSED:  { color: GREEN, border: GREEN_BORDER, bg: GREEN_BG, label: "Closed"  },
  PENDING: { color: BLUE,  border: BLUE_BORDER,  bg: BLUE_BG,  label: "Pending" },
};

export const OPD_STATUS = {
  Outpatient: { label: "Outpatient", color: "blue"   },
  Inpatient:  { label: "Inpatient",  color: "purple" },
  Emergency:  { label: "Emergency",  color: "red"    },
};

// ── NEW v3: Gradients ─────────────────────────────────────────
export const GRAD_BRAND   = `linear-gradient(135deg, ${G700} 0%, ${G500} 100%)`;
export const GRAD_HERO    = `linear-gradient(118deg, ${G900} 0%, ${G800} 46%, ${G700} 100%)`;
export const GRAD_SURFACE = `linear-gradient(180deg, ${WHITE} 0%, ${G50} 140%)`;
export const GRAD_TEXT    = `linear-gradient(90deg, ${G700}, ${G500})`;

// ── 8pt spacing scale ─────────────────────────────────────────
export const SPACE = {
  "0": "0px",  "1": "4px",  "2": "8px",  "3": "12px",
  "4": "16px", "5": "20px", "6": "24px", "7": "28px",
  "8": "32px", "10": "40px","12": "48px","16": "64px",
  "20": "80px",
};

// ── Breakpoints ───────────────────────────────────────────────
export const BP = {
  mobile:  480,
  tablet:  768,
  laptop:  1024,
  desktop: 1280,
  wide:    1536,
};

// ── Shadow scale ──────────────────────────────────────────────
export const SHADOW_XS = "0 1px 2px rgba(15,28,46,0.04)";
export const SHADOW_SM = "0 1px 2px rgba(15,28,46,0.04), 0 2px 8px rgba(15,28,46,0.05)";
export const SHADOW_MD = "0 2px 6px rgba(15,28,46,0.05), 0 8px 24px rgba(15,28,46,0.07)";
export const SHADOW_LG = "0 8px 24px rgba(15,28,46,0.08), 0 24px 48px rgba(15,28,46,0.10)";
export const SHADOW_XL = "0 16px 40px rgba(15,28,46,0.10), 0 32px 80px rgba(15,28,46,0.12)";
export const SHADOW_BLUE = "0 6px 20px rgba(14,91,194,0.22)";
export const SHADOW_INNER = "inset 0 1px 2px rgba(15,28,46,0.06)";

// NEW v3 — glow & hero shadows
export const SHADOW_GLOW      = "0 0 0 1px rgba(14,91,194,0.08), 0 8px 28px rgba(14,91,194,0.16)";
export const SHADOW_HERO      = "0 12px 32px rgba(0,45,107,0.28), 0 32px 72px rgba(0,45,107,0.18)";
export const SHADOW_BTN_WHITE = "0 4px 14px rgba(0,0,0,0.16)";

// ── Radius scale ──────────────────────────────────────────────
export const RADIUS_XS = 6;
export const RADIUS_SM = 10;
export const RADIUS_MD = 14;
export const RADIUS_LG = 20;
export const RADIUS_XL = 28;
export const RADIUS_PILL = 999;

// ── Easing ────────────────────────────────────────────────────
export const EASE_OUT    = "cubic-bezier(0.16, 1, 0.3, 1)";
export const EASE_IN_OUT = "cubic-bezier(0.65, 0, 0.35, 1)";
export const EASE_STD    = "cubic-bezier(0.4, 0, 0.2, 1)";
export const TRANSITION_FAST = `all 0.12s ${EASE_STD}`;
export const TRANSITION_SM   = `all 0.18s ${EASE_OUT}`;
export const TRANSITION_MD   = `all 0.28s ${EASE_OUT}`;
export const TRANSITION_LG   = `all 0.42s ${EASE_OUT}`;

// ── Z-index ───────────────────────────────────────────────────
export const Z = {
  base: 0, raised: 1, sticky: 40, topbar: 50,
  sidebar: 100, overlay: 190, drawer: 200, modal: 300, toast: 400,
};

// ── Type scale ────────────────────────────────────────────────
export const FS = {
  "10": "10px","11": "11px","12": "12px","13": "13px","14": "14px",
  "15": "15px","16": "16px","17": "17px","19": "19px","22": "22px",
  "26": "26px","32": "32px","40": "40px",
};

export const FF_DISPLAY = "'Figtree', system-ui, sans-serif";
export const FF_BODY    = "'Inter', system-ui, sans-serif";
export const FF_MONO    = "'JetBrains Mono', 'Fira Code', 'Menlo', monospace";

export const TOUCH_MIN = 44;

// ─────────────────────────────────────────────────────────────────
//  injectPatientStyles()  — v3
// ─────────────────────────────────────────────────────────────────
export function injectPatientStyles() {
  if (document.getElementById("pd-styles")) return;

  const link = document.createElement("link");
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Figtree:wght@500;600;700;800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap";
  document.head.appendChild(link);

  const style = document.createElement("style");
  style.id = "pd-styles";
  style.textContent = `
    *, *::before, *::after { box-sizing: border-box; }
    button { font-family: inherit; cursor: pointer; -webkit-tap-highlight-color: transparent; }
    :focus-visible { outline: 2px solid ${G700}; outline-offset: 2px; border-radius: 6px; }
    ::selection { background: ${G200}; }

    .pd-root {
      font-family: ${FF_BODY};
      font-size: 14px;
      line-height: 1.5;
      color: ${SLATE};
      background:
        radial-gradient(1200px 500px at 85% -10%, rgba(14,91,194,0.06), transparent 60%),
        radial-gradient(900px 420px at -10% 30%, rgba(34,211,238,0.05), transparent 55%),
        ${SURF};
      height: 100vh; height: 100dvh;
      display: flex;
      overflow: hidden;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
    }

    /* ── Sidebar ───────────────────────────────────────────── */
    .pd-sidebar {
      width: 256px; min-width: 256px;
      background: ${WHITE};
      border-right: 1px solid ${GRAY6};
      display: flex; flex-direction: column;
      height: 100%; overflow: hidden;
      transition: width ${TRANSITION_MD}, min-width ${TRANSITION_MD};
      flex-shrink: 0;
      z-index: ${Z.sidebar};
    }
    .pd-sidebar.collapsed { width: 72px; min-width: 72px; }
    .pd-sidebar-drawer {
      position: fixed; top: 0; left: 0; height: 100%;
      transform: translateX(-100%);
      transition: transform 0.32s ${EASE_OUT};
      z-index: ${Z.drawer};
      box-shadow: ${SHADOW_XL};
    }
    .pd-sidebar-drawer.open { transform: translateX(0); }
    .pd-sidebar-overlay {
      position: fixed; inset: 0;
      background: rgba(10,20,40,0.4);
      backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
      z-index: ${Z.overlay};
      display: none;
      animation: pdFade 0.2s ease both;
    }
    @keyframes pdFade { from { opacity: 0; } to { opacity: 1; } }

    /* ── Topbar — glass ─────────────────────────────────────── */
    .pd-topbar {
      height: 62px; min-height: 62px;
      background: rgba(255,255,255,0.72);
      backdrop-filter: blur(22px) saturate(180%);
      -webkit-backdrop-filter: blur(22px) saturate(180%);
      border-bottom: 1px solid ${GRAY6};
      display: flex; align-items: center; justify-content: space-between;
      padding: 0 28px;
      position: sticky; top: 0; z-index: ${Z.topbar};
      gap: 16px; flex-shrink: 0;
    }

    /* ── Main scroll area ─────────────────────────────────── */
    .pd-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; min-width: 0; }
    .pd-scroll {
      flex: 1; overflow-y: auto; overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: thin; scrollbar-color: ${GRAY6} transparent;
    }
    .pd-scroll::-webkit-scrollbar { width: 6px; }
    .pd-scroll::-webkit-scrollbar-thumb { background: ${GRAY6}; border-radius: 10px; }
    .pd-scroll::-webkit-scrollbar-thumb:hover { background: ${GRAY4}; }

    .pd-page-content {
      padding: 28px 32px 88px;
      max-width: 1180px; width: 100%; margin: 0 auto;
      animation: pdFadeUp 0.32s ${EASE_OUT} both;
    }
    @keyframes pdFadeUp {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0);   }
    }

    .pd-eyebrow {
      font-size: 11px; font-weight: 700; letter-spacing: 1.2px;
      text-transform: uppercase; color: ${G700};
      display: inline-flex; align-items: center; gap: 6px;
      margin-bottom: 6px; font-family: ${FF_BODY};
    }

    .pd-status {
      display: inline-flex; align-items: center; gap: 6px;
      font-size: 11.5px; font-weight: 600;
      padding: 3px 10px 3px 8px; border-radius: ${RADIUS_PILL}px;
      font-family: ${FF_BODY};
    }

    /* ── NEW v3: Hero card ──────────────────────────────────── */
    .pd-hero {
      position: relative;
      background: ${GRAD_HERO};
      border-radius: ${RADIUS_XL}px;
      box-shadow: ${SHADOW_HERO};
      overflow: hidden;
      color: ${WHITE};
      isolation: isolate;
    }
    .pd-hero::before {
      content: '';
      position: absolute; inset: 0; z-index: -1;
      background:
        radial-gradient(560px 260px at 88% -20%, rgba(34,211,238,0.22), transparent 60%),
        radial-gradient(420px 300px at -6% 110%, rgba(65,137,220,0.30), transparent 55%);
    }
    .pd-hero-grid {
      position: absolute; inset: 0; z-index: -1; opacity: 0.35;
      background-image:
        linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px);
      background-size: 36px 36px;
      mask-image: radial-gradient(720px 340px at 70% 20%, #000 30%, transparent 78%);
      -webkit-mask-image: radial-gradient(720px 340px at 70% 20%, #000 30%, transparent 78%);
    }
    /* Signature: heartbeat trace that draws itself once on load */
    .pd-ekg-path {
      stroke-dasharray: 1200;
      stroke-dashoffset: 1200;
      animation: pdEkgDraw 2.4s ${EASE_OUT} 0.25s forwards;
    }
    @keyframes pdEkgDraw { to { stroke-dashoffset: 0; } }

    .pd-hero-btn-primary {
      background: ${WHITE}; color: ${G800}; border: none;
      box-shadow: ${SHADOW_BTN_WHITE};
      transition: ${TRANSITION_SM};
    }
    .pd-hero-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(0,0,0,0.22); }
    .pd-hero-btn-ghost {
      background: rgba(255,255,255,0.10); color: ${WHITE};
      border: 1px solid rgba(255,255,255,0.28);
      backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
      transition: ${TRANSITION_SM};
    }
    .pd-hero-btn-ghost:hover { background: rgba(255,255,255,0.18); transform: translateY(-1px); }

    /* ── NEW v3: Stat cards ─────────────────────────────────── */
    .pd-stat-card {
      position: relative;
      background: ${GRAD_SURFACE};
      border: 1px solid ${GRAY6};
      border-radius: ${RADIUS_MD}px;
      box-shadow: ${SHADOW_SM};
      overflow: hidden;
      transition: transform ${TRANSITION_MD}, box-shadow ${TRANSITION_MD}, border-color ${TRANSITION_MD};
    }
    .pd-stat-card::after {
      content: '';
      position: absolute; top: 0; left: 0; right: 0; height: 3px;
      background: var(--pd-accent, ${G700});
      transform: scaleX(0); transform-origin: left;
      transition: transform 0.3s ${EASE_OUT};
      border-radius: 3px 3px 0 0;
    }
    .pd-stat-card:hover { transform: translateY(-3px); box-shadow: ${SHADOW_MD}; border-color: ${G200}; }
    .pd-stat-card:hover::after { transform: scaleX(1); }
    .pd-stat-value { font-variant-numeric: tabular-nums; }

    /* ── NEW v3: Panel & list rows ──────────────────────────── */
    .pd-panel {
      background: ${WHITE};
      border: 1px solid ${GRAY6};
      border-radius: ${RADIUS_LG}px;
      box-shadow: ${SHADOW_SM};
      overflow: hidden;
      display: flex; flex-direction: column; height: 100%;
      transition: box-shadow ${TRANSITION_MD};
    }
    .pd-panel:hover { box-shadow: ${SHADOW_MD}; }

    .pd-row {
      display: flex; align-items: center; gap: 12px;
      padding: 12px 16px; min-height: 56px;
      border-bottom: 1px solid ${GRAY7};
      transition: background ${TRANSITION_SM}, padding-left ${TRANSITION_SM};
      position: relative;
    }
    .pd-row:last-child { border-bottom: none; }
    .pd-row.clickable { cursor: pointer; }
    .pd-row.clickable::before {
      content: ''; position: absolute; left: 0; top: 0; bottom: 0;
      width: 3px; background: ${G700};
      transform: scaleY(0);
      transition: transform 0.2s ${EASE_OUT};
    }
    .pd-row.clickable:hover { background: ${G50}; padding-left: 20px; }
    .pd-row.clickable:hover::before { transform: scaleY(1); }
    .pd-row.clickable:hover .pd-row-arrow { transform: translateX(3px); color: ${G700}; }
    .pd-row-arrow { transition: transform ${TRANSITION_SM}, color ${TRANSITION_SM}; }

    .pd-date-tile {
      width: 44px; height: 46px; border-radius: ${RADIUS_SM}px;
      background: ${G50}; border: 1px solid ${G100};
      display: flex; flex-direction: column;
      align-items: center; justify-content: center; flex-shrink: 0;
      transition: ${TRANSITION_SM};
    }
    .pd-row.clickable:hover .pd-date-tile {
      background: ${GRAD_BRAND}; border-color: transparent;
      box-shadow: ${SHADOW_BLUE};
    }
    .pd-row.clickable:hover .pd-date-tile .pd-dt-day,
    .pd-row.clickable:hover .pd-date-tile .pd-dt-mon { color: ${WHITE}; }
    .pd-dt-day, .pd-dt-mon { transition: color ${TRANSITION_SM}; }

    .pd-view-all {
      background: none; border: none; color: ${G700};
      font-weight: 600; font-size: 11.5px; font-family: ${FF_BODY};
      display: inline-flex; align-items: center; gap: 4px;
      padding: 6px 10px; border-radius: ${RADIUS_SM}px;
      min-height: 32px;
      transition: ${TRANSITION_SM};
    }
    .pd-view-all:hover { background: ${G50}; gap: 7px; }

    /* ── NEW v3: skeleton shimmer (optional loading states) ─── */
    .pd-skeleton {
      background: linear-gradient(90deg, ${GRAY7} 25%, #fafcff 50%, ${GRAY7} 75%);
      background-size: 200% 100%;
      animation: pdShimmer 1.4s ease-in-out infinite;
      border-radius: 8px;
    }
    @keyframes pdShimmer { to { background-position: -200% 0; } }

    .pd-pulse { animation: pdPulse 4s ease-in-out infinite; }
    @keyframes pdPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(14,91,194,0.22); }
      50%      { box-shadow: 0 0 0 6px rgba(14,91,194,0); }
    }
    .pd-live-dot {
      width: 7px; height: 7px; border-radius: 50%;
      background: ${GREEN_MID}; display: inline-block;
      box-shadow: 0 0 0 0 rgba(34,166,106,0.5);
      animation: pdLive 2.4s ease-out infinite;
    }
    @keyframes pdLive {
      0%   { box-shadow: 0 0 0 0 rgba(34,166,106,0.45); }
      70%  { box-shadow: 0 0 0 7px rgba(34,166,106,0);  }
      100% { box-shadow: 0 0 0 0 rgba(34,166,106,0);    }
    }

    .pd-up { animation: pdUp 0.36s ${EASE_OUT} both; }
    .pd-up-1 { animation-delay: 0.05s; }
    .pd-up-2 { animation-delay: 0.10s; }
    .pd-up-3 { animation-delay: 0.15s; }
    .pd-up-4 { animation-delay: 0.20s; }
    @keyframes pdUp {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: translateY(0);    }
    }

    .pd-visit-row:hover td { background: ${G50} !important; }
    .pd-visit-row td { transition: background 0.12s; }

    .pd-card-hover { transition: transform ${TRANSITION_MD}, box-shadow ${TRANSITION_MD}; }
    .pd-card-hover:hover { transform: translateY(-2px); box-shadow: ${SHADOW_MD}; }

    /* ── Bottom tab bar ───────────────────────────────────── */
    .pd-bottom-nav {
      display: none; position: fixed; bottom: 0; left: 0; right: 0;
      background: rgba(255,255,255,0.92);
      backdrop-filter: blur(24px) saturate(180%);
      -webkit-backdrop-filter: blur(24px) saturate(180%);
      border-top: 1px solid ${GRAY6};
      z-index: ${Z.sidebar};
      padding: 0 8px calc(env(safe-area-inset-bottom));
    }

    /* ── Responsive tiers ─────────────────────────────────── */
    @media (max-width: ${BP.laptop}px) {
      .pd-page-content { max-width: 100%; padding: 24px 24px 88px; }
    }
    @media (max-width: ${BP.tablet}px) {
      .pd-sidebar:not(.pd-sidebar-drawer) { display: none; }
      .pd-bottom-nav { display: block; }
      .pd-main { padding-bottom: calc(56px + env(safe-area-inset-bottom)); }
      .pd-topbar { padding: 0 16px; height: 56px; min-height: 56px; }
      .pd-page-content { padding: 18px 16px 84px; }
    }
    @media (max-width: ${BP.mobile}px) {
      .pd-page-content { padding: 14px 12px 84px; }
      .pd-topbar { padding: 0 12px; }
    }

    @media (prefers-reduced-motion: reduce) {
      .pd-up, .pd-pulse, .pd-page-content, .pd-live-dot,
      .pd-sidebar, .pd-ekg-path, .pd-skeleton,
      .pd-stat-card, .pd-row, .pd-card-hover {
        animation: none !important; transition: none !important;
      }
      .pd-ekg-path { stroke-dashoffset: 0 !important; }
    }
  `;
  document.head.appendChild(style);
}